import {Injectable} from '@angular/core';
import {combineLatest, filter, map, Observable, of, switchMap, take, tap} from 'rxjs';
import {login} from '@modules/auth/store/actions/login.actions';
import {Store} from '@ngrx/store';
import {LocalStorageService} from '@shared/services/local-storage.service';
import {
  selectAuthState,
  selectImpersonatingUser,
  selectIsRegisterSuccess,
  selectIsResendVerificationEmailSuccess,
  selectIsResetPasswordError,
  selectIsResetPasswordSuccess,
  selectIsSendResetPasswordsLinkError,
  selectIsSendResetPasswordsLinkSuccess,
  selectIsVerifyEmailError,
  selectIsVerifyEmailSuccess,
  selectLoginErrorMessages,
  selectLoginLoading,
  selectMustVerifyEmail,
  selectRegisterError,
  selectRegisterLoading,
  selectResendVerificationEmailLoading,
  selectResetPasswordErrorMessages,
  selectResetPasswordLoading,
  selectSendResetPasswordsLinkError,
  selectSendResetPasswordsLinkLoading,
  selectUser,
  selectVerifyEmailLoading,
} from '@modules/auth/store/features/auth.feature';
import {
  authFeatureName,
  AuthState, authUserFeature, impersonatingUserFeatureName,
} from '@modules/auth/store/reducers/auth.reducer';
import {userFactory} from '@modules/user/factories/user.factory';
import {
  LoginInput,
  MeQuery,
  RegisterInput,
  RegisterVariables,
  ResetPasswordInput,
  SendResetPasswordLinkVariables,
  UserType,
  VerifyEmailInput,
} from '@generated/graphql';
import {logout} from '@modules/auth/store/actions/logout.actions';
import {registerActions} from '@modules/auth/store/actions/register.actions';
import {resendEmailVerificationActions} from '@modules/auth/store/actions/resend-email-verification.actions';
import {verifyEmailActions} from '@modules/auth/store/actions/verify-email.actions';
import {resettingPasswordActions} from '@modules/auth/store/actions/resetting-password.actions';
import {ValidationError} from '@utils/validation/app.validation';
import {impersonateActions} from '@modules/auth/store/actions/impersonate.actions';
import {authActions} from '@modules/auth/store/actions/auth.actions';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  returnUrl: string;

  readonly auth$: Observable<AuthState> = this.store.select(selectAuthState)

  readonly authUser$: Observable<UserType> = this.store.select(selectUser)

  readonly impersonatingUser$: Observable<UserType> = this.store.select(selectImpersonatingUser)

  readonly loginErrorMessages$: Observable<string[]> = this.store.select(selectLoginErrorMessages)

  readonly registerError$: Observable<ValidationError<RegisterVariables>> = this.store.select(selectRegisterError)

  readonly mustVerifyEmail$: Observable<boolean> = this.store.select(selectMustVerifyEmail)

  readonly loginLoading$: Observable<boolean> = this.store.select(selectLoginLoading)

  readonly resendVerificationEmailLoading$: Observable<boolean> = this.store.select(selectResendVerificationEmailLoading)

  readonly registerLoading$: Observable<boolean> = this.store.select(selectRegisterLoading)

  readonly isRegisterSuccess$: Observable<boolean> = this.store.select(selectIsRegisterSuccess)

  readonly isResendVerificationEmailSuccess$: Observable<boolean> = this.store.select(selectIsResendVerificationEmailSuccess)

  readonly verifyEmailLoading$: Observable<boolean> = this.store.select(selectVerifyEmailLoading)

  readonly isVerifyEmailSuccess$: Observable<boolean> = this.store.select(selectIsVerifyEmailSuccess)

  readonly isVerifyEmailError$: Observable<boolean> = this.store.select(selectIsVerifyEmailError)

  readonly sendResetPasswordsLinkLoading$: Observable<boolean> = this.store.select(selectSendResetPasswordsLinkLoading)

  readonly isSendResetPasswordsLinkSuccess$: Observable<boolean> = this.store.select(selectIsSendResetPasswordsLinkSuccess)

  readonly isSendResetPasswordsLinkError$: Observable<boolean> = this.store.select(selectIsSendResetPasswordsLinkError)

  readonly sendResetPasswordsLinkError$: Observable<ValidationError<SendResetPasswordLinkVariables>> = this.store.select(selectSendResetPasswordsLinkError)

  readonly resetPasswordLoading$: Observable<boolean> = this.store.select(selectResetPasswordLoading)

  readonly isResetPasswordSuccess$: Observable<boolean> = this.store.select(selectIsResetPasswordSuccess)

  readonly isResetPasswordError$: Observable<boolean> = this.store.select(selectIsResetPasswordError)

  readonly resetPasswordErrorMessages$: Observable<string[]> = this.store.select(selectResetPasswordErrorMessages)

  activeUser$ = combineLatest([
    this.impersonatingUser$,
    this.authUser$
  ]).pipe(
    map(([impersonateUser, authUser]) =>
      userFactory.tryCreating(impersonateUser || authUser)
    ),
  );

  constructor(
    private readonly store: Store,
    private readonly localStorageService: LocalStorageService,
    private readonly _fetchMe: MeQuery,
  ) {
  }

  impersonate(id: string): void {
    this.store.dispatch(impersonateActions.impersonateUser({id}));
  }

  reloadActiveUser$(): Observable<UserType> {
    return this.fetchMe()
      .pipe(
        // filter(user => !!user),
        tap(user => {
          this.setActiveUser(user)
        }),
      );
  }

  setActiveUser(user?: UserType | null) {
    this.store.dispatch(authActions.setAuth({
      auth: {
        [authUserFeature]: user?.isImpersonating ? user.impersonator : user,
        [impersonatingUserFeatureName]: user?.isImpersonating ? user : null
      }
    }))

    this.syncAuthToLocalStorage()
  }

  syncAuthToLocalStorage() {
    this.auth$.pipe(
      take(1),
      tap(auth => this.setAuthToLocalStorage(auth))
    ).subscribe()
  }

  updateActiveUser(value: Partial<UserType>): void {
    try {
      const updatedUser = {...value} as UserType;

      this.setActiveUser(updatedUser);
    } catch (e) {
      console.error(e);
    }
  }

  stopImpersonate(): void {
    this.store.dispatch(impersonateActions.stopImpersonate());
  }

  login(input: LoginInput): void {
    this.store.dispatch(login({input}));
  }

  register(input: RegisterInput): void {
    this.store.dispatch(registerActions.register({input}));
  }

  refreshRegisterState(): void {
    this.store.dispatch(registerActions.refreshState());
  }

  logout(): void {
    this.store.dispatch(logout());
  }

  resetAuth(): void {
    this.store.dispatch(authActions.resetAuth());
  }

  verifyEmail(input: VerifyEmailInput): void {
    this.store.dispatch(verifyEmailActions.verifyEmail({input}));
  }

  resendEmailVerification(email: string): void {
    this.store.dispatch(
      resendEmailVerificationActions.resendVerificationEmail({
        emailOrId: email,
      }),
    );
  }

  sendResetPasswordsLink(email: string): void {
    this.store.dispatch(resettingPasswordActions.sendLink({email}));
  }

  resetPassword(input: ResetPasswordInput): void {
    this.store.dispatch(resettingPasswordActions.resetPassword({input}));
  }

  refreshResettingPasswordState(): void {
    this.store.dispatch(resettingPasswordActions.refreshState());
  }

  async getAuthFromLocalStorage(): Promise<AuthState> {
    return await this.localStorageService.getData<AuthState>(authFeatureName);
  }

  async setAuthToLocalStorage(auth: AuthState): Promise<AuthService> {
    await this.localStorageService.saveData(authFeatureName, auth);

    return this;
  }

  async resetAuthFromLocalStorage(): Promise<AuthService> {
    await this.localStorageService.removeData(authFeatureName);

    return this;
  }

  fetchMe(): Observable<UserType> {
    return this._fetchMe
      .fetch(null, {
        fetchPolicy: 'no-cache',
      }).pipe(map(({data}) => data.me as UserType));
  }
}
